<template>
  <Modal
    v-model="visible"
    :title="modalTitle"
    @on-cancel="handleCancel"
    :mask-closable="false"
    width="820"
  >
    <Form v-if="visible" ref="formRef" :model="formData" :label-width="120">
      <FormItem
        label="推送活动"
        prop="activityConfigId"
        :rules="[
          {
            required: true,
            type: 'number',
            message: '请选择推送活动',
            trigger: 'change'
          }
        ]"
      >
        <Select
          v-model="formData.activityConfigId"
          placeholder="请输入活动名称搜索"
          clearable
          filterable
          remote
          :remote-method="searchActivities"
          :loading="activityLoading"
          :disabled="readonly"
          @on-change="handleActivityChange"
        >
          <Option
            v-for="item in activityOptions"
            :key="item.id"
            :value="item.id"
            :label="item.activityName"
          >
            {{ item.activityName }}
          </Option>
        </Select>
      </FormItem>

      <FormItem label="推送人群">
        <RadioGroup
          v-model="formData.crowdLimit"
          type="button"
          button-style="solid"
          :disabled="readonly"
          @on-change="handleCrowdLimitChange"
        >
          <Radio :label="0" :disabled="readonly">不限</Radio>
          <Radio :label="1" :disabled="readonly">选择人群包</Radio>
        </RadioGroup>
        <Select
          v-if="formData.crowdLimit === 1"
          v-model="formData.crowdIds"
          placeholder="请选择人群包"
          multiple
          filterable
          :loading="crowdLoading"
          :disabled="readonly || !formData.activityConfigId"
          style="margin-top: 10px"
        >
          <Option
            v-for="item in availableCrowdOptions"
            :key="item.crowdId"
            :value="item.crowdId"
            :label="item.crowdName"
          >
            {{ item.crowdName }}
          </Option>
        </Select>
        <div
          v-if="
            formData.crowdLimit === 1 &&
            !crowdLoading &&
            crowdEmptyTip &&
            !availableCrowdOptions.length
          "
          class="field-tip"
        >
          {{ crowdEmptyTip }}
        </div>
      </FormItem>

      <FormItem label="活动入口">
        <div
          v-for="(entry, index) in formData.entries"
          :key="'activity-entry-' + index"
          class="entry-block"
        >
          <div class="entry-row">
            <span class="entry-label">活动入口{{ index + 1 }}</span>
            <div class="entry-field-group">
              <Select
                v-model="entry.entryType"
                placeholder="请选择活动入口"
                :disabled="readonly"
                @on-change="(value) => handleEntryTypeChange(index, value)"
              >
                <Option
                  v-for="item in activityEntryTypeOptions"
                  :key="'entry-type-' + item.value"
                  :value="item.value"
                  :disabled="isEntryTypeUsed(item.value, index)"
                >
                  {{ item.label }}
                </Option>
              </Select>
              <Select
                v-if="requiresEntryStyle(entry.entryType)"
                v-model="entry.entryStyle"
                :placeholder="getEntryStyleLabel(entry.entryType)"
                :disabled="readonly || !entry.entryType"
              >
                <Option
                  v-for="item in getEntryStyleOptions(entry.entryType)"
                  :key="'entry-style-' + entry.entryType + '-' + item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </Option>
              </Select>
            </div>
            <Icon
              v-if="!readonly && formData.entries.length > 1"
              type="ios-close-circle"
              class="entry-remove-icon"
              @click="removeEntry(index)"
            />
          </div>

          <div v-if="requiresEntryUpload(entry.entryType)" class="entry-upload-row">
            <template v-if="readonly">
              <div v-if="entry.imageFileUrl" class="readonly-image">
                <img
                  :src="entry.imageFileUrl"
                  class="readonly-image-preview"
                  @click="$viewerApi({ images: [entry.imageFileUrl] })"
                />
              </div>
              <div v-else class="readonly-empty">未上传图片</div>
            </template>
            <PopupUpload
              v-else
              v-model="entry.imageFileUrl"
              :width="requiresExactSize(entry.entryType) ? '140px' : '112px'"
              :height="requiresExactSize(entry.entryType) ? '140px' : '112px'"
              :hint="getUploadHint(entry.entryType)"
              :max-size="100"
              :exact-width="requiresExactSize(entry.entryType) ? 140 : undefined"
              :exact-height="requiresExactSize(entry.entryType) ? 140 : undefined"
              @on-success="(data) => handleEntryUploadSuccess(index, data)"
              @on-remove="() => handleEntryUploadRemove(index)"
            />
          </div>

          <div
            v-else-if="readonly && requiresEntryStyle(entry.entryType)"
            class="entry-upload-row"
          >
            <div class="readonly-empty">
              {{ getEntryStyleText(entry.entryType, entry.entryStyle) || '未选择图片' }}
            </div>
          </div>
        </div>

        <div
          v-if="!readonly && formData.entries.length < maxEntryCount"
          class="add-entry-btn"
          @click="addEntry"
        >
          + 添加入口
        </div>
      </FormItem>

      <FormItem
        label="优先级"
        prop="priority"
        :rules="[
          {
            required: true,
            type: 'number',
            message: '请输入优先级',
            trigger: 'change'
          }
        ]"
      >
        <InputNumber
          v-model="formData.priority"
          :min="1"
          :max="9999"
          :precision="0"
          style="width: 200px"
          :disabled="readonly"
        />
      </FormItem>

      <FormItem
        label="推送规则名称"
        prop="ruleName"
        :rules="[
          { required: true, message: '请输入推送规则名称', trigger: 'blur' }
        ]"
      >
        <Input
          v-model="formData.ruleName"
          placeholder="仅用于后台显示"
          :maxlength="100"
          :disabled="readonly"
        />
      </FormItem>
    </Form>

    <template #footer v-if="!readonly">
      <Button @click="handleCancel">取消</Button>
      <Button type="primary" :loading="loading" @click="handleSubmit">
        保存
      </Button>
    </template>
  </Modal>
</template>

<script>
import { debounce } from 'lodash'
import PopupUpload from '@/components/Upload/PopupUpload.vue'
import {
  addActivityPushRule,
  editActivityPushRule,
  getActivityConfigOptions,
  getActivityPushCrowds
} from '@/api/activityPush'
import {
  ACTIVITY_ENTRY_TYPE_OPTIONS,
  MAX_ACTIVITY_ENTRY_COUNT,
  buildDefaultActivityEntry,
  getActivityEntryStyleLabel,
  getActivityEntryStyleOptions,
  getActivityEntryStyleText,
  requiresActivityEntryStyle,
  requiresActivityEntryUpload
} from './activityPushOptions'

export default {
  name: 'H5ActivityEditModal',
  components: {
    PopupUpload
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      activityLoading: false,
      crowdLoading: false,
      activityOptions: [],
      availableCrowdOptions: [],
      crowdEmptyTip: '',
      dailyTriggerLimitMode: 0,
      formData: {
        id: '',
        activityConfigId: null,
        activityName: '',
        crowdLimit: 0,
        crowdIds: [],
        dailyTriggerLimit: -1,
        priority: 1,
        ruleName: '',
        entries: [buildDefaultActivityEntry(1)]
      }
    }
  },
  computed: {
    modalTitle() {
      if (this.readonly) return '查看H5活动推送规则'
      return (this.formData.id ? '编辑' : '新增') + 'H5活动推送'
    },
    maxEntryCount() {
      return MAX_ACTIVITY_ENTRY_COUNT
    },
    activityEntryTypeOptions() {
      return ACTIVITY_ENTRY_TYPE_OPTIONS
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.visible = val
        if (val) {
          this.initializeModal()
        }
      }
    },
    visible(val) {
      this.$emit('input', val)
    }
  },
  methods: {
    async initializeModal() {
      await this.loadActivityOptions('')
      if (this.data && this.data.id) {
        this.mapDataToForm(this.data)
        this.ensureActivityOption(
          this.formData.activityConfigId,
          this.formData.activityName
        )
        await this.loadCrowdOptions(this.formData.activityConfigId)
        this.ensureCrowdOptionsFromDetail(this.data)
        this.filterInvalidCrowdIds()
      } else {
        this.resetFormData()
      }
    },
    normalizeActivityOption(item = {}) {
      return {
        id: item.id,
        activityName: item.activityName
      }
    },
    async loadActivityOptions(query = '') {
      this.activityLoading = true
      try {
        const { data } = await getActivityConfigOptions({
          current: 1,
          size: 100,
          activityName: query || undefined
        })
        this.activityOptions = (data.records || data.list || data || []).map(
          (item) => this.normalizeActivityOption(item)
        )
      } catch (error) {
        this.$Message.error('获取活动列表失败')
      } finally {
        this.activityLoading = false
      }
    },
    searchActivities: debounce(function (query) {
      this.loadActivityOptions(query)
    }, 300),
    ensureActivityOption(id, activityName) {
      if (!id || !activityName) return
      const exists = this.activityOptions.some(
        (item) => Number(item.id) === Number(id)
      )
      if (!exists) {
        this.activityOptions.unshift({
          id: Number(id),
          activityName
        })
      }
    },
    async loadCrowdOptions(activityConfigId) {
      if (!activityConfigId) {
        this.availableCrowdOptions = []
        this.crowdEmptyTip = ''
        return
      }
      this.crowdLoading = true
      try {
        const { data } = await getActivityPushCrowds(activityConfigId)
        this.availableCrowdOptions = (data.crowdList || []).map((item) => ({
          crowdId: item.crowdId,
          crowdName: item.crowdName
        }))
        this.crowdEmptyTip = data.emptyTip || ''
      } catch (error) {
        this.availableCrowdOptions = []
        this.crowdEmptyTip = ''
        this.$Message.error('获取活动可选人群包失败')
      } finally {
        this.crowdLoading = false
      }
    },
    ensureCrowdOptionsFromDetail(data = {}) {
      const crowdIds = Array.isArray(data.crowdIds) ? data.crowdIds : []
      const crowdNames = String(data.crowdNames || '')
        .split(/[、,]/)
        .map((item) => item.trim())
        .filter(Boolean)
      crowdIds.forEach((crowdId, index) => {
        const exists = this.availableCrowdOptions.some(
          (item) => Number(item.crowdId) === Number(crowdId)
        )
        if (!exists) {
          this.availableCrowdOptions.push({
            crowdId: Number(crowdId),
            crowdName: crowdNames[index] || `人群包${crowdId}`
          })
        }
      })
    },
    filterInvalidCrowdIds() {
      const validIds = new Set(
        this.availableCrowdOptions.map((item) => String(item.crowdId))
      )
      this.formData.crowdIds = (this.formData.crowdIds || []).filter((item) =>
        validIds.has(String(item))
      )
    },
    async handleActivityChange(activityConfigId) {
      const selected = this.activityOptions.find(
        (item) => Number(item.id) === Number(activityConfigId)
      )
      this.formData.activityName = selected ? selected.activityName : ''
      this.formData.crowdIds = []
      await this.loadCrowdOptions(activityConfigId)
    },
    handleCrowdLimitChange(value) {
      if (Number(value) !== 1) {
        this.formData.crowdIds = []
      }
    },
    getEntryStyleOptions(entryType) {
      return getActivityEntryStyleOptions(entryType)
    },
    getEntryStyleLabel(entryType) {
      return getActivityEntryStyleLabel(entryType)
    },
    getEntryStyleText(entryType, entryStyle) {
      return getActivityEntryStyleText(entryType, entryStyle)
    },
    requiresEntryStyle(entryType) {
      return requiresActivityEntryStyle(entryType)
    },
    requiresEntryUpload(entryType) {
      return requiresActivityEntryUpload(entryType)
    },
    requiresExactSize(entryType) {
      return Number(entryType) === 1 || Number(entryType) === 2
    },
    getUploadHint(entryType) {
      if (this.requiresExactSize(entryType)) {
        return '140x140，支持透明底，jpg/png格式，<100KB'
      }
      return 'jpg/png 格式, <100KB'
    },
    isEntryTypeUsed(value, currentIndex) {
      return this.formData.entries.some((item, index) => {
        return index !== currentIndex && Number(item.entryType) === Number(value)
      })
    },
    normalizeEntry(entry = {}, index = 0) {
      const entryType =
        entry.entryType === 0 || entry.entryType
          ? Number(entry.entryType)
          : ''
      const shouldShowStyle = this.requiresEntryStyle(entryType)
      return {
        entryType,
        entryStyle: 1,
        imageFileId:
          entry.imageFileId === 0 || entry.imageFileId
            ? Number(entry.imageFileId)
            : '',
        imageFileUrl: entry.imageFileUrl || '',
        sortNum: Number(entry.sortNum || index + 1)
      }
    },
    syncEntrySortNum() {
      this.formData.entries.forEach((item, index) => {
        this.$set(item, 'sortNum', index + 1)
      })
    },
    addEntry() {
      if (this.formData.entries.length >= this.maxEntryCount) {
        this.$Message.warning(`最多添加${this.maxEntryCount}个活动入口`)
        return
      }
      this.formData.entries.push(
        buildDefaultActivityEntry(this.formData.entries.length + 1)
      )
    },
    removeEntry(index) {
      this.formData.entries.splice(index, 1)
      if (!this.formData.entries.length) {
        this.formData.entries = [buildDefaultActivityEntry(1)]
      }
      this.syncEntrySortNum()
    },
    handleEntryTypeChange(index, value) {
      const entry = this.formData.entries[index]
      if (!entry) return
      this.$set(entry, 'entryType', value ? Number(value) : '')
      if (this.requiresEntryStyle(value)) {
        this.$set(entry, 'imageFileId', '')
        this.$set(entry, 'imageFileUrl', '')
        const styleOptions = this.getEntryStyleOptions(value)
        this.$set(
          entry,
          'entryStyle',
          styleOptions.length ? styleOptions[0].value : ''
        )
      } else {
        this.$set(entry, 'entryStyle', '')
      }
      if (this.requiresEntryUpload(value)) {
        this.$set(entry, 'imageFileId', '')
        this.$set(entry, 'imageFileUrl', '')
      }
    },
    handleEntryUploadSuccess(index, data) {
      const entry = this.formData.entries[index]
      if (!entry || !data) return
      this.$set(entry, 'imageFileId', data.fileId || data.id || '')
      this.$set(entry, 'imageFileUrl', data.fileUrl || entry.imageFileUrl)
    },
    handleEntryUploadRemove(index) {
      const entry = this.formData.entries[index]
      if (!entry) return
      this.$set(entry, 'imageFileId', '')
      this.$set(entry, 'imageFileUrl', '')
    },
    handleDailyLimitModeChange(value) {
      if (Number(value) === 0) {
        this.formData.dailyTriggerLimit = -1
      } else if (
        !this.formData.dailyTriggerLimit ||
        Number(this.formData.dailyTriggerLimit) <= 0
      ) {
        this.formData.dailyTriggerLimit = 1
      }
    },
    mapDataToForm(data = {}) {
      const dailyTriggerLimit =
        data.dailyTriggerLimit === 0 || data.dailyTriggerLimit
          ? Number(data.dailyTriggerLimit)
          : -1
      this.formData = {
        id: data.id || '',
        activityConfigId:
          data.activityConfigId === 0 || data.activityConfigId
            ? Number(data.activityConfigId)
            : null,
        activityName: data.activityName || '',
        crowdLimit: Number(data.crowdLimit || 0),
        crowdIds: (data.crowdIds || []).map((item) => Number(item)),
        dailyTriggerLimit,
        priority:
          data.priority === 0 || data.priority ? Number(data.priority) : 1,
        ruleName: data.ruleName || '',
        entries: Array.isArray(data.entries) && data.entries.length
          ? data.entries.map((item, index) => this.normalizeEntry(item, index))
          : [buildDefaultActivityEntry(1)]
      }
      this.dailyTriggerLimitMode = dailyTriggerLimit === -1 ? 0 : 1
      this.syncEntrySortNum()
    },
    resetFormData() {
      this.formData = {
        id: '',
        activityConfigId: null,
        activityName: '',
        crowdLimit: 0,
        crowdIds: [],
        dailyTriggerLimit: -1,
        priority: 1,
        ruleName: '',
        entries: [buildDefaultActivityEntry(1)]
      }
      this.availableCrowdOptions = []
      this.crowdEmptyTip = ''
      this.dailyTriggerLimitMode = 0
    },
    validateEntries() {
      if (!this.formData.entries.length) {
        this.$Message.warning('请至少添加一个活动入口')
        return false
      }

      for (let index = 0; index < this.formData.entries.length; index++) {
        const entry = this.formData.entries[index]
        if (!entry.entryType) {
          this.$Message.warning(`请选择活动入口${index + 1}的入口类型`)
          return false
        }
        if (
          this.requiresEntryStyle(entry.entryType) &&
          entry.entryStyle !== 0 &&
          !entry.entryStyle
        ) {
          this.$Message.warning(`请选择活动入口${index + 1}的图片`)
          return false
        }
        if (this.requiresEntryUpload(entry.entryType) && !entry.imageFileId) {
          this.$Message.warning(`请上传活动入口${index + 1}的图片`)
          return false
        }
      }

      return true
    },
    validateDynamicFields() {
      if (!this.formData.activityConfigId) {
        this.$Message.warning('请选择推送活动')
        return false
      }
      if (this.formData.crowdLimit === 1 && !this.formData.crowdIds.length) {
        this.$Message.warning('请选择人群包')
        return false
      }
      if (
        this.dailyTriggerLimitMode === 1 &&
        (!this.formData.dailyTriggerLimit ||
          Number(this.formData.dailyTriggerLimit) <= 0)
      ) {
        this.$Message.warning('请输入有效的每日触发次数')
        return false
      }
      return this.validateEntries()
    },
    buildSubmitPayload() {
      const payload = {
        activityConfigId: Number(this.formData.activityConfigId),
        crowdLimit: Number(this.formData.crowdLimit),
        crowdIds:
          Number(this.formData.crowdLimit) === 1
            ? this.formData.crowdIds.map((item) => Number(item))
            : [],
        dailyTriggerLimit:
          this.dailyTriggerLimitMode === 0
            ? -1
            : Number(this.formData.dailyTriggerLimit),
        priority: Number(this.formData.priority),
        ruleName: this.formData.ruleName.trim(),
        entries: this.formData.entries.map((item, index) => {
          const entry = {
            entryType: Number(item.entryType),
            sortNum: index + 1,
            entryStyle: 1
          }
          if (this.requiresEntryUpload(item.entryType)) {
            entry.imageFileId = Number(item.imageFileId)
          }
          return entry
        })
      }

      if (this.formData.id) {
        payload.id = Number(this.formData.id)
      }

      return payload
    },
    handleCancel() {
      this.visible = false
    },
    handleSubmit() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        if (!this.validateDynamicFields()) return

        this.loading = true
        try {
          const submitData = this.buildSubmitPayload()
          const apiFunc = submitData.id
            ? editActivityPushRule
            : addActivityPushRule
          const result = await apiFunc(submitData)
          if (result.message === 'Success' || result.code === 200) {
            this.$Message.success('保存成功')
          } else {
            this.$Message.warning(result.message || '保存成功')
          }
          this.visible = false
          this.$emit('on-success')
        } catch (error) {
          this.$Message.error('保存失败')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped lang="less">
.entry-block {
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.entry-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.entry-label {
  width: 70px;
  line-height: 32px;
  color: #515a6e;
  flex-shrink: 0;
}

.entry-field-group {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.entry-remove-icon {
  margin-top: 8px;
  color: #ed4014;
  cursor: pointer;
  font-size: 18px;
}

.entry-upload-row {
  margin-top: 12px;
  margin-left: 82px;
}

.add-entry-btn {
  color: #2d8cf0;
  cursor: pointer;
  font-size: 13px;
}

.field-tip {
  margin-top: 6px;
  color: #999;
  font-size: 12px;
  line-height: 1.5;
}

.readonly-image {
  display: inline-flex;
  align-items: center;
}

.readonly-image-preview {
  width: 112px;
  height: 112px;
  border-radius: 4px;
  object-fit: cover;
  cursor: pointer;
}

.readonly-empty {
  color: #999;
  font-size: 12px;
}

@media (max-width: 768px) {
  .entry-row {
    flex-direction: column;
  }

  .entry-label {
    width: auto;
    line-height: 1.5;
  }

  .entry-field-group {
    width: 100%;
    grid-template-columns: 1fr;
  }

  .entry-upload-row {
    margin-left: 0;
  }
}
</style>
